import { useEffect,useState } from 'react'
import { Period } from '../../../../types/dataModel';
import {API} from '../../../../common/Constants'
import useFetch from '../../../../hooks/useFetch';
import PreLoader from '../../../../core/common/preLoader';
import { getAPIHeaders } from '../../../../common/Utils';
import { Link } from 'react-router-dom'
import CommonSelect2 from '../../../../core/common/commonSelectV2';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import FormFooter from '../../../../core/common/formFooter';

interface Props {
  period?:Period,
  subjects: any[],
  employees: any[],
  onSuccess: ()=> void,
  isUpdate?: boolean,
}

const ModifyPeriod = ({onSuccess, period, subjects, employees, isUpdate}:Props) => {
  const [formPeriod, setFormPeriod] = useState<Period>()
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const {isLoading, error, data, status, callFetch } = useFetch();
  const [showMsgBox, setShowMsgBox] = useState<boolean>(false)

  useEffect(()=>{
    if (period) {
      // Format time values to HH:MM format when initializing
      setFormPeriod({
        ...period,
        startTime: formatToHHMM(period.startTime),
        endTime: formatToHHMM(period.endTime)
      });
    }
  },[period])

  useEffect(() => {
          if(data && status && status < 299){
              reset();
              setShowMsgBox(true)
              onSuccess()
          }
  }, [status]);

    useEffect(() => {
        if(error){
          if(error.response?.data){
            setErrorMessage(error.response?.data)
          } else{
            setErrorMessage('Something went wrong please try again later.')
          }
        }
    }, [error]);

  const handleSubmit = (event:any) => {
      event.preventDefault()
      if(!formPeriod?.courseSubjectId){
        setErrorMessage("Please select a subject name")
      } else if(!formPeriod?.employeeId){
        setErrorMessage("Please select a faculty")
      } else if(!formPeriod?.startTime || !formPeriod?.endTime) {
        setErrorMessage("Please set both start and end times")
      }  else if(formPeriod?.startTime && formPeriod?.endTime && !isValidTimeRange(formPeriod.startTime, formPeriod.endTime)) {
        setErrorMessage("End time must be after start time");
      } else {
        setErrorMessage("")
        // Format times to ensure HH:MM format before sending to backend
        const formattedPeriod = {
          ...formPeriod,
          startTime: formatToHHMM(formPeriod.startTime),
          endTime: formatToHHMM(formPeriod.endTime)
        };
        
        // Use PUT method for updates, POST for new entries
        const method = isUpdate ? 'PUT' : 'POST';
        
        callFetch(API.TIMETABLE,
            method,
            getAPIHeaders(),
            null,
            formattedPeriod
        ).catch(()=>{          
        });
      }
    }

    const isValidTimeRange = (startTime: string, endTime: string): boolean => {
      const start = dayjs(startTime, 'HH:mm');
      const end = dayjs(endTime, 'HH:mm');
      
      // Special case: if end time is 00:00 (midnight), it's valid regardless of start time
      // But not if start time is also 00:00
      if (end.format('HH:mm') === '00:00' && start.format('HH:mm') !== '00:00') {
        return true;
      }
      
      // Special case: if start time is in PM (afternoon/evening) and end time is in AM (morning)
      // we assume it crosses midnight
      const startHour = parseInt(start.format('HH'));
      const endHour = parseInt(end.format('HH'));
      
      if (startHour >= 12 && endHour < 12) {
        return true;
      }
      
      // Normal case: end time should be after start time
      return end.isAfter(start);
    }

    const reset = () => {
      setFormPeriod({
        ...formPeriod,
        courseSubjectId: undefined,
        employeeId: undefined
      })
      setErrorMessage("")
      setShowMsgBox(false)
    }

    const formatToHHMM = (time: string | undefined): string | undefined => {
      if (!time) return undefined;
      
      // If time includes seconds (HH:MM:SS format), strip them out
      if (/^\d{1,2}:\d{2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(':');
        return `${hours.padStart(2, '0')}:${minutes}`;
      }
      
      // If it's already in HH:MM format, ensure proper padding
      if (/^\d{1,2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(':');
        return `${hours.padStart(2, '0')}:${minutes}`;
      }
      
      // If it's another format, convert to HH:MM
      try {
        const timeObj = dayjs(time);
        if (timeObj.isValid()) {
          return timeObj.format('HH:mm');
        }
      } catch (e) {
        console.error('Error formatting time:', e);
      }
      
      return time;
    }

    const renderPeriodForm = () => {
      return (
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{isUpdate ? 'Update Period' : 'Add Period'}</h4>
            Day: {formPeriod?.day} Period:{formPeriod?.period}
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={reset}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form>
            <div className="modal-body pb-0">
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Subject <span className="text-danger"> *</span></label>
                    <CommonSelect2
                      options={subjects}
                      defaultValue={subjects.find(s => s.value === formPeriod?.courseSubjectId) || null}
                      onChange={(value: any) => {
                        setFormPeriod({
                          ...formPeriod,
                          courseSubjectId: parseInt(value)
                        })
                      }}                        
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Faculty <span className="text-danger"> *</span></label>
                    <CommonSelect2
                      options={employees}
                      defaultValue={employees.find(e => e.value === formPeriod?.employeeId) || null}
                      onChange={(value: any) => {
                        setFormPeriod({
                          ...formPeriod,
                          employeeId: parseInt(value)
                        })
                      }}                        
                    />
                  </div>
                </div>
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Time Slot <span className="text-danger"> *</span></label>
                    <div className="d-flex align-items-center">
                      <TimePicker 
                        format="HH:mm"
                        placeholder="Start Time"
                        className="me-2"
                        value={formPeriod?.startTime ? dayjs(formPeriod.startTime, 'HH:mm') : null}
                        onChange={(time) => {
                          if (time) {
                            const timeString = time.format('HH:mm');
                            setFormPeriod({
                              ...formPeriod,
                              startTime: timeString
                            });
                            
                            // If end time exists and is earlier than new start time, update it
                            if (formPeriod?.endTime) {
                              const endTime = dayjs(formPeriod.endTime, 'HH:mm');
                              if (time.isAfter(endTime)) {
                                // Set end time to 1 hour after start time
                                const newEndTime = time.add(1, 'hour').format('HH:mm');
                                setFormPeriod(prevState => ({
                                  ...prevState,
                                  endTime: newEndTime
                                }));
                              }
                            }
                          }
                        }}
                      />
                      <span className="mx-2">to</span>
                      <TimePicker 
                        format="HH:mm"
                        placeholder="End Time"
                        value={formPeriod?.endTime ? dayjs(formPeriod.endTime, 'HH:mm') : null}
                        onChange={(time) => {
                          if (time) {
                            // Make sure the selected end time is valid
                            if (formPeriod?.startTime && 
                                !isValidTimeRange(formPeriod.startTime, time.format('HH:mm'))) {
                                setErrorMessage("End time must be after start time");
                              return;
                            }
                            
                            setErrorMessage("");
                            const timeString = time.format('HH:mm');
                            setFormPeriod({
                              ...formPeriod,
                              endTime: timeString
                            });
                          }
                        }}
                      />
                    </div>
                    {formPeriod?.startTime && formPeriod?.endTime && 
                      !isValidTimeRange(formPeriod.startTime, formPeriod.endTime) && (
                      <div className="text-danger mt-1" style={{ fontSize: '12px' }}>
                        <i className="ti ti-alert-circle me-1"></i>
                        End time must be after start time
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ 
                justifyContent: "space-between",
                flexDirection:'row'}}>
                  <FormFooter 
                    onSave={handleSubmit}
                    onCancel={reset}
                    message={errorMessage}
                    error={!!errorMessage}
                  />
            </div>
          </form>
        </div>
      )
    }

    const renderMsgBox = () => {
      return (          
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center">
              <span className="avatar avatar-xl bg-transparent-success text-success mb-3">
                  <i className="ti ti-thumb-up fs-36" />
              </span>
              <h4 className="mb-1">{isUpdate ? 'Update Period Successful' : 'Add Period Successful'}</h4>
              <p className="mb-3">
                  Period is {isUpdate ? 'updated' : 'added'} successfully in time table for {formPeriod?.day}.
              </p>
              <div className="d-flex justify-content-center">
                  <Link to="#" 
                      data-bs-dismiss="modal" 
                      className="btn btn-success"
                      onClick={()=>{
                        setTimeout(() => {
                          setShowMsgBox(false);
                        }, 1000);
                        }}>
                      Close
                  </Link>
              </div>
          </div>
      </div>
    </div>)
    }

    return (
      <div className="modal-dialog modal-dialog-centered modal-md"
      data-bs-backdrop="static"
      data-bs-keyboard="false">
        <PreLoader showLoader={isLoading} />
        {showMsgBox ? renderMsgBox() : renderPeriodForm()}
      </div>
    )
}

export default  ModifyPeriod